import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISSED_KEY = 'rpa.installPromptDismissed';

/**
 * One-line dismissible install banner shown on the Dashboard for first-time
 * mobile visitors. Handles both platforms cleanly:
 *
 *  • Chrome / Android / Edge fire `beforeinstallprompt`. We capture it,
 *    expose an "Install app" button, and call .prompt() on click.
 *  • iOS Safari doesn't fire that event. We detect iOS Safari and instead
 *    show "Tap [Share] → Add to Home Screen" inline.
 *  • If the app is already running standalone (installed) we render nothing.
 *  • If the user has dismissed the banner, we render nothing for 60 days.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Already installed → never show.
    const standalone =
      window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = window.navigator.standalone === true;
    if (standalone || iosStandalone) return;

    // Recently dismissed → keep hidden for 60 days.
    try {
      const ts = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
      if (ts && Date.now() - ts < 60 * 24 * 60 * 60 * 1000) return;
    } catch (_) {}

    // Only show on small screens — desktop already gets the address-bar install icon.
    if (window.innerWidth > 900) return;

    // Detect iOS Safari (no beforeinstallprompt support).
    const ua = window.navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const inSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    if (iOS && inSafari) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installed = () => setVisible(false);
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch (_) {}
  };

  const onInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="install-banner" role="region" aria-label="Install app" data-testid="install-banner">
      <div className="install-banner-icon"><Download size={18} /></div>
      <div className="install-banner-text">
        <strong>Install Rental P.A. on your phone</strong>
        {isIOS ? (
          <span className="install-banner-hint">
            Tap <Share size={14} style={{ verticalAlign: 'middle' }} /> Share → <em>Add to Home Screen</em>
          </span>
        ) : (
          <span className="install-banner-hint">One-tap install — opens fullscreen, no app store needed.</span>
        )}
      </div>
      {!isIOS && (
        <button
          onClick={onInstall}
          className="install-banner-cta"
          data-testid="install-banner-cta"
        >
          Install
        </button>
      )}
      <button
        onClick={dismiss}
        className="install-banner-close"
        aria-label="Dismiss"
        data-testid="install-banner-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
